import { useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../../auth/AuthContext';
import { Card } from '../../shared/components/Card';
import { Field, Input } from '../../shared/components/Field';
import { PasswordInput } from '../../shared/components/PasswordInput';
import { Button } from '../../shared/components/Button';
import { Alert, Spinner } from '../../shared/components/feedback';
import { ApiError } from '../../api/client';

interface LocationState {
  from?: { pathname?: string };
}

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as LocationState)?.from?.pathname ?? '/board';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (loading) return;
    setError(null);
    setLoading(true);
    try {
      await login({ email, password });
      navigate(from, { replace: true });
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : '로그인에 실패했습니다. 이메일과 비밀번호를 확인해 주세요.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <Title>로그인</Title>
      <Subtitle>예약한 콘텐츠와 계정 현황을 이어서 확인하세요.</Subtitle>

      <Form onSubmit={handleSubmit} aria-busy={loading}>
        {error && <Alert tone="danger">{error}</Alert>}

        <Field label="이메일" htmlFor="email">
          <Input
            id="email"
            type="email"
            autoComplete="email"
            autoCapitalize="none"
            spellCheck={false}
            disabled={loading}
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </Field>

        <Field label="비밀번호" htmlFor="password">
          <PasswordInput
            id="password"
            disabled={loading}
            autoComplete="current-password"
            placeholder="비밀번호를 입력하세요"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </Field>

        <Button type="submit" $size="lg" $fullWidth disabled={loading}>
          {loading ? <><Spinner aria-hidden="true" /> 로그인 중…</> : '로그인'}
        </Button>
      </Form>

      <Footer>
        아직 계정이 없으신가요? <Link to="/register">회원가입</Link>
      </Footer>
      <LegalLinks>
        <LegalLink to="/privacy">개인정보 처리방침</LegalLink>
        <span aria-hidden>·</span>
        <LegalLink to="/data-deletion">데이터 삭제</LegalLink>
      </LegalLinks>
    </Card>
  );
}

const Title = styled.h1`
  font-size: 28px;
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  letter-spacing: -0.02em;
`;

const Subtitle = styled.p`
  margin-top: 6px;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const Form = styled.form`
  margin-top: 22px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Footer = styled.p`
  margin-top: 20px;
  text-align: center;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textSecondary};

  a {
    color: ${({ theme }) => theme.colors.primary};
    font-weight: 600;
  }
`;

const LegalLinks = styled.div`
  margin-top: 14px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const LegalLink = styled(Link)`
  color: ${({ theme }) => theme.colors.textMuted};

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;
